/**
 * Join codes. SPEC.md §6.2.
 *
 * Short, dictatable, case-insensitive, and free of the O/0 and I/1 confusions.
 * Codes are half letters then half digits ("FOX284", shown as "FOX-284") which
 * reads naturally out loud across a noisy table.
 */

import {
  DEFAULT_JOIN_CODE_LENGTH,
  JOIN_CODE_DIGITS,
  JOIN_CODE_LETTERS,
} from './constants.js';

/** Returns a uniformly distributed integer in [0, max). */
export type RandomInt = (maxExclusive: number) => number;

const mathRandomInt: RandomInt = (max) => Math.floor(Math.random() * max);

/**
 * Generates a code. Callers on the server pass a CSPRNG-backed `randomInt`; the
 * default is only suitable for tests and local tooling.
 */
export function generateJoinCode(
  length: number = DEFAULT_JOIN_CODE_LENGTH,
  randomInt: RandomInt = mathRandomInt,
): string {
  const total = Math.max(2, Math.floor(length));
  const letterCount = Math.ceil(total / 2);
  const digitCount = total - letterCount;

  let code = '';
  for (let i = 0; i < letterCount; i += 1) {
    code += JOIN_CODE_LETTERS[randomInt(JOIN_CODE_LETTERS.length)];
  }
  for (let i = 0; i < digitCount; i += 1) {
    code += JOIN_CODE_DIGITS[randomInt(JOIN_CODE_DIGITS.length)];
  }
  return code;
}

/**
 * Canonical storage/lookup form: uppercase, alphanumeric only. Accepts anything
 * a player might type or paste — "fox-284", "FOX 284", " fox284 ". §6.2
 */
export function normalizeJoinCode(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** Whether a normalized code uses only the unambiguous alphabet. */
export function isValidJoinCode(input: string): boolean {
  const normalized = normalizeJoinCode(input);
  if (normalized.length < 4 || normalized.length > 16) return false;
  for (const ch of normalized) {
    if (!JOIN_CODE_LETTERS.includes(ch) && !JOIN_CODE_DIGITS.includes(ch)) return false;
  }
  return true;
}

/** Display form with a single separating dash: "FOX284" -> "FOX-284". */
export function formatJoinCode(code: string): string {
  const normalized = normalizeJoinCode(code);
  if (normalized.length < 4) return normalized;
  const split = Math.ceil(normalized.length / 2);
  return `${normalized.slice(0, split)}-${normalized.slice(split)}`;
}

/** Canonical join URL for sharing and QR codes. §6.1, §6.2 */
export function joinUrl(origin: string, code: string): string {
  const base = origin.replace(/\/+$/, '');
  return `${base}/j/${normalizeJoinCode(code)}`;
}
